import Corporate from "@/components/Corporate";
import SEO from "@/components/SEO";
import {
  filterDataToSingleItem,
  usePreviewSubscription,
} from "@/lib/sanity/client";
import {
  mainLayoutQuery,
  MainLayoutQuery,
  settingsQuery,
  corporatePageQuery,
  seoQuerySchema,
} from "@/lib/sanity/queries";
import { getClient } from "@/lib/sanity/server";
import { mergeWith } from "lodash";
import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { groq } from "next-sanity";
import { useEffect, useMemo } from "react";
import { z } from "zod";

type UrlQuery = {
  slug: string;
};

export const getStaticPaths: GetStaticPaths<UrlQuery> = async () => {
  const query = groq`*[_type == "corporatePage" && defined(slug.current)][].slug.current`;
  const slugs = await getClient().fetch<string[]>(query);
  return {
    paths: slugs.map((slug) => "/corporate/" + slug),
    fallback: true,
  };
};

const pageDataSchema = z.object({
  _id: z.string(), // required for preview
  seo: seoQuerySchema,
  paragraphIndex: z
    .array(
      z
        .object({
          _key: z.string(),
          _type: z.string(),
          heading: z.string(),
          slug: z.string(),
        })
        .passthrough()
    )
    .nullable(),
  pageBuilder: z
    .array(z.object({ _key: z.string(), _type: z.string() }).passthrough())
    .nullable(),
});

type PageData = z.infer<typeof pageDataSchema>;

type Props = {
  preview: boolean;
  data: {
    page: PageData;
    query: string;
    queryParams: {
      slug: string;
      locale?: string;
      defaultLocale?: string;
    };
  };
  settings: any;
  layout: MainLayoutQuery;
};

export const getStaticProps: GetStaticProps<Props, UrlQuery> = async (ctx) => {
  const { locale = "it", defaultLocale = "it", preview = false, params } = ctx;

  const slug = params?.slug;
  if (!slug) return { notFound: true };

  const queryParams = { slug, locale, defaultLocale };
  const res = await getClient(preview).fetch(corporatePageQuery, queryParams);
  const data = z.array(pageDataSchema).parse(res);

  // Escape hatch, if our query failed to return data
  if (!data?.length) return { notFound: true };

  // Helper function to reduce all returned documents down to just one
  const page = filterDataToSingleItem<PageData>(data, preview);

  const [settings, layout] = await Promise.all([
    // Get site settings
    getClient(false).fetch(settingsQuery, {
      locale,
      defaultLocale,
    }),
    // Get Layout data
    getClient(false).fetch(mainLayoutQuery, {
      locale,
      defaultLocale,
    }),
  ]);

  return {
    props: {
      preview,
      settings,
      layout,
      data: {
        page,
        query: corporatePageQuery,
        queryParams,
      },
    },
    revalidate: 60,
  };
};

const Page: NextPage<Props> = ({ data, settings, preview }) => {
  const { data: previewData, error } = usePreviewSubscription(data?.query, {
    params: data?.queryParams,
    // The hook will return this on first render
    // This is why it's important to fetch *draft* content server-side!
    initialData: data?.page,
    // The passed-down preview context determines whether this function does anything
    enabled: preview,
  });

  useEffect(() => {
    if (error) console.error(error);
  }, [error]);

  // Client-side uses the same query, so we may need to filter it down again
  const page = filterDataToSingleItem(previewData, preview);
  const pageBuilder = page?.pageBuilder || null;
  const paragraphIndex = page?.paragraphIndex || null;

  // Merge page seo with general seo
  const seo = useMemo(() => {
    if (typeof page?.seo !== "object") return {};
    if (typeof settings?.seo !== "object") return {};
    return mergeWith(settings.seo, page.seo, (a, b) =>
      b === null ? a : undefined
    );
  }, [page?.seo, settings?.seo]);

  return (
    <>
      {seo && <SEO {...seo} slug={data?.queryParams?.slug} />}
      <Corporate paragraphIndex={paragraphIndex} pageBuilder={pageBuilder} />
    </>
  );
};

export default Page;
